import { Lexer, Marked } from 'marked';
import type { Token, Tokens } from 'marked';
import { markdownToTxt } from 'markdown-to-txt';
import { ReactElement } from 'react';
import classNames from 'classnames';
import { isSafeHref } from 'utils/urlScheme';
import styles from './RenderMarkdownMarked.module.scss';
import { buildSafeOpeningLinkTag, escapeAttr, isClosingLinkTag } from './safeLinks';

// Markdown Implementation copied from Rails (required as ReactMarkdown has too much security for onClick handlers + iframes)

const applyLineBreaks = (source: string) =>
  source.replace(
    /(?:\r\n|\n){2}((?:\r\n|\n)+)/g,
    (match: string, group: string) => `\n\n${group.replace(/\r\n|\n/g, '<br/>\n\n')}`,
  );

// marked v9+ emits the deprecated `align` HTML attribute for GFM table alignment, which
// browsers treat as a presentational hint that the CSS `text-align: left` rule overrides.
// This renderer converts it to an inline style so alignment survives the CSS cascade.
function tablecell(
  this: { parser: { parseInline: (tokens: Tokens.TableCell['tokens']) => string } },
  token: Tokens.TableCell,
): string {
  const tag = token.header ? 'th' : 'td';
  const style = token.align ? ` style="text-align: ${token.align}"` : '';
  const body = this.parser.parseInline(token.tokens);
  return `<${tag}${style}>${body}</${tag}>\n`;
}

const markedInstance = new Marked({
  gfm: true,
  breaks: true,
  renderer: { tablecell },
});

// The builder's own underline button has no markdown equivalent, so it writes `<u>`, and an
// author may paste a bare `<br>` line break from elsewhere. Dropping every raw tag would silently
// discard both, so the attribute-less tags a paste (or the toolbar) can emit survive; everything
// else, including any tag bearing an attribute, does not.
const PASSTHROUGH_HTML = /^<\/?(u|em|strong|b|i)>$|^<br\s*\/?>$/i;

/**
 * How many raw `<a>` tags this renderer has opened and not yet closed in the current parse. A
 * raw tag's opening and closing half are sanitized independently (`safeLinks.ts`), so an author
 * who drops one half — an unclosed `<a>` in a plain textarea, or a stray `</a>` that would
 * otherwise close a markdown-generated link early — must not unbalance the output: a dangling
 * open tag reopens an anchor around whatever renders next to this block, and an unmatched closing
 * tag can truncate a link's own text. Reset per parse in `hooks.preprocess`, since one `Marked`
 * instance is reused across every call to `.parse()`.
 */
let openRawAnchorCount = 0;

/**
 * `script`, `style` and `textarea` are CommonMark's "literal" tags: marked reads everything
 * between the open and close tag as plain text rather than markup, whether the whole element
 * stands alone as its own block or sits inline mid-sentence. Dropping just the tags, as for any
 * other unrecognized raw tag, would still surface that text on the page, so the whole span is cut
 * from the source before tokenizing, regardless of which way marked would have read it.
 */
const OPAQUE_HTML_ELEMENT = /<(script|style|textarea)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

/**
 * Author markdown is untrusted content that ends up in a `dangerouslySetInnerHTML` sink —
 * `marked` passes raw inline/block HTML straight through by default, which is how a `<script>`
 * or an `onerror`/`onclick` attribute would ride in. This renderer drops raw HTML tokens, keeping
 * only the toolbar's formatting tags and raw `<a>` links rebuilt from a safe address
 * (`safeLinks.ts`), and only emits links on an http/https/mailto/tel scheme or a relative address
 * (a `javascript:` link renders as its text instead), leaving markdown's own generated tags —
 * headings, tables, lists, emphasis — untouched.
 */
const sanitizedMarkedInstance = new Marked({
  gfm: true,
  breaks: true,
  hooks: {
    preprocess(markdown: string): string {
      openRawAnchorCount = 0;
      return markdown.replace(OPAQUE_HTML_ELEMENT, '');
    },
    postprocess(html: string): string {
      if (openRawAnchorCount === 0) return html;
      const closed = `${html}${'</a>'.repeat(openRawAnchorCount)}`;
      openRawAnchorCount = 0;
      return closed;
    },
  },
  renderer: {
    tablecell,
    html(this: { parser: { parseInline: (tokens: Token[]) => string } }, token: Tokens.HTML | Tokens.Tag): string {
      const raw = token.raw.trim();
      if (PASSTHROUGH_HTML.test(raw)) return raw;
      if (isClosingLinkTag(raw)) {
        if (openRawAnchorCount === 0) return '';
        openRawAnchorCount -= 1;
        return '</a>';
      }
      const opening = buildSafeOpeningLinkTag(raw);
      if (opening !== null) {
        if (opening) openRawAnchorCount += 1;
        return opening;
      }
      // A block the author pasted as HTML (a CMS's `<p>…</p>`, or a link on its own lines) arrives
      // as one token covering the whole block. `pre` marks the block as one marked reads verbatim
      // (script, style, textarea, a fenced `<pre>`) rather than a wrapper around ordinary content;
      // re-lexing that verbatim text as inline markdown would surface it as visible text instead
      // of removing it, so only a non-`pre` block is unwrapped this way — its own tags fall back
      // to this same renderer, keeping its text and any safe link rather than losing the block.
      if (!('pre' in token) || !token.block || token.pre) return '';
      return this.parser.parseInline(Lexer.lexInline(token.text, sanitizedMarkedInstance.defaults));
    },
    link(this: { parser: { parseInline: (tokens: Tokens.Link['tokens']) => string } }, token: Tokens.Link): string {
      const text = this.parser.parseInline(token.tokens);
      if (!isSafeHref(token.href)) return text;
      const title = token.title ? ` title="${escapeAttr(token.title)}"` : '';
      return `<a href="${escapeAttr(token.href)}"${title}>${text}</a>`;
    },
  },
});

const renderMarkdown = (source: string, sanitize?: boolean) =>
  (sanitize ? sanitizedMarkedInstance : markedInstance).parse(applyLineBreaks(source));

/**
 * Convert markdown to plain text (no HTML or markdown syntax).
 * Use for plain-text contexts (e.g. structured data). Server-safe (no jsdom).
 * @param source - Markdown string
 * @returns Plain text string
 */
export function markdownToPlainText(source: string): string {
  if (typeof source !== 'string' || !source.trim()) return '';
  // markdown-to-txt returns string per its type definitions
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- markdownToTxt(markdown: string): string
  return (markdownToTxt(source) as string).trim();
}

interface Props {
  text: string;
  className?: string;
  /**
   * Strips raw HTML and non-web link schemes before rendering. Off by default because some
   * existing consumers rely on intentional onclick handlers in their markdown — a new sink for
   * untrusted, author-supplied text should opt in rather than changing that default.
   */
  sanitize?: boolean;
}

export function RenderMarkdownMarked(props: Props): ReactElement | null {
  const { text, className, sanitize } = props;

  const html = renderMarkdown(text, sanitize) as string;
  // Sanitizing can remove everything a non-blank source produced (a lone `<script>`, or HTML
  // marked reads as a block with no safe content inside it); mounting an empty container then
  // would still take up a flex gap or margin a caller reserved only for an actual description.
  if (!html.trim()) return null;
  return <div className={classNames(styles.container, className)} dangerouslySetInnerHTML={{ __html: html }} />;
}
